import { motion, AnimatePresence } from 'framer-motion';
import { X, Server, CheckCircle, Zap, Snowflake, MapPin } from 'lucide-react';

interface InfrastructureRedondanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InfrastructureRedondanceModal({ isOpen, onClose }: InfrastructureRedondanceModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-cyan-500/30 shadow-2xl shadow-cyan-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-cyan-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl">
                    <Server className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                      Infrastructure & Redondance
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Standards de qualité élevés - Redondance N+1 complète
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-cyan-600/20 rounded-xl p-8 border-2 border-cyan-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Infrastructure Robuste et Redondante
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  Notre infrastructure offre des <strong className="text-white">standards de qualité très élevés</strong> avec 
                  <strong className="text-cyan-400"> redondance N+1</strong> sur tous les éléments critiques : génératrices, UPS, climatisation. 
                  Deux centres de données géographiquement séparés pour une disponibilité maximale.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-7 h-7 text-cyan-400" />
                    Redondance Génératrice N+1
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Alimentation de secours garantie avec redondance N+1 :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Génératrices multiples</strong> - Redondance complète</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Test régulier</strong> - Maintenance préventive</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Démarrage automatique</strong> - Basculement transparent</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Capacité de réserve</strong> - Marge de sécurité</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-7 h-7 text-blue-400" />
                    UPS N+1
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Alimentation sans interruption avec redondance N+1 :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Batteries multiples</strong> - Redondance complète</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Temps d'autonomie</strong> - Protection pendant les coupures</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Monitoring continu</strong> - Surveillance de l'état des batteries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Maintenance préventive</strong> - Remplacement proactif</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Snowflake className="w-7 h-7 text-indigo-400" />
                    Climatisation Redondante
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Contrôle de température et d'humidité avec redondance :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Systèmes multiples</strong> - Redondance N+1</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Température contrôlée</strong> - Optimale pour les serveurs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Humidité contrôlée</strong> - Protection contre l'humidité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Monitoring continu</strong> - Alertes en temps réel</span>
                    </li>
                  </ul>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-green-400" />
                      Centre de Données BZ
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Notre centre de données dans les locaux de BZ Inc. offre une infrastructure complète 
                      avec tous nos standards de qualité et redondance N+1.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                    <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-blue-400" />
                      Centre de Données Oricom (Serko)
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Notre deuxième centre de données chez Oricom, salle Serko, offre redondance géographique 
                      et haute disponibilité avec les mêmes standards de qualité.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-cyan-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












